/**
 * Ads Optimizer — Marketing Landing Page
 * Self-contained, CSS-isolated React component (scope: .ado-lp).
 * DS components (Button/Tag/Input/Terminal) are inline subcomponents
 * that emit the same class names as the SchababerleDigitalDesignSystem
 * bundle — no window global required.
 */
import { useState, useEffect, useRef } from 'react';
import './landing.css';
import logoSrc from './assets/logo-prompt.svg';
import werkbankSrc from './assets/werkbank-detail.png';
import dashboardSrc from './assets/01-dashboard.png';

/* ================================================================
   Inline DS subcomponents
   ================================================================ */

/* ----- Button ----- */
type BtnVariant = 'primary' | 'gradient' | 'outline' | 'secondary' | 'ghost' | 'danger';
type BtnSize = 'sm' | 'md' | 'lg';

interface BtnProps {
  variant: BtnVariant;
  size?: BtnSize;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  block?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
}

function Btn({ variant, size = 'md', href, type = 'button', block, disabled, icon, children, onClick }: BtnProps) {
  const cls = [
    'sd-btn',
    `sd-btn--${variant}`,
    size !== 'md' ? `sd-btn--${size}` : null,
    block ? 'sd-btn--block' : null,
  ].filter(Boolean).join(' ');

  const inner = (
    <>
      {icon && <span className="sd-btn__icon">{icon}</span>}
      {children && <span className="sd-btn__label">{children}</span>}
    </>
  );

  if (href && !disabled) {
    return (
      <a className={cls} href={href} onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}>
        {inner}
      </a>
    );
  }
  return (
    <button className={cls} type={type} disabled={disabled} onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}>
      {inner}
    </button>
  );
}

/* ----- Tag ----- */
interface TagProps {
  icon?: React.ReactNode;
  children?: React.ReactNode;
  active?: boolean;
  interactive?: boolean;
}

function Tag({ icon, children, active, interactive }: TagProps) {
  const cls = [
    'sd-tag',
    active ? 'sd-tag--active' : null,
    interactive ? 'sd-tag--interactive' : null,
  ].filter(Boolean).join(' ');
  return (
    <span className={cls}>
      {icon && <span className="sd-tag__icon">{icon}</span>}
      {children}
    </span>
  );
}

/* ----- Input / Field ----- */
interface FieldProps {
  label?: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

function Field({ label, type = 'text', required, placeholder, icon, value, onChange }: FieldProps) {
  return (
    <div className="sd-field">
      {label && (
        <label className="sd-field__label">
          {label}
          {required && <span className="sd-field__req">*</span>}
        </label>
      )}
      <div className="sd-field__wrap">
        {icon && <span className="sd-field__icon">{icon}</span>}
        <input
          className={['sd-input', icon ? 'sd-input--has-icon' : null].filter(Boolean).join(' ')}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
        />
      </div>
    </div>
  );
}

/* ----- Terminal ----- */
type TermLineType = 'cmd' | 'out' | 'link';

interface TermLine {
  type: TermLineType;
  text: string;
  href?: string;
  prompt?: string;
}

interface TermProps {
  title?: string;
  lines?: TermLine[];
  cursor?: boolean;
}

function Term({ title, lines = [], cursor = true }: TermProps) {
  return (
    <div className="sd-term">
      {title && (
        <div className="sd-term__bar">
          <span className="sd-term__dot sd-term__dot--r" />
          <span className="sd-term__dot sd-term__dot--y" />
          <span className="sd-term__dot sd-term__dot--g" />
          <span className="sd-term__title">{title}</span>
        </div>
      )}
      <div className="sd-term__body">
        {lines.map((line, i) => {
          if (line.type === 'out') {
            return (
              <div key={i} className="sd-term__line sd-term--out">
                {line.text}
              </div>
            );
          }
          if (line.type === 'link') {
            return (
              <div key={i} className="sd-term__line">
                <a className="sd-term--link" href={line.href ?? '#'}>
                  {line.text}
                </a>
              </div>
            );
          }
          /* cmd */
          const parts = line.text.split(' ');
          const cmd = parts[0] ?? '';
          const rest = parts.slice(1).join(' ');
          return (
            <div key={i} className="sd-term__line">
              <span className="sd-term__prompt">$</span>
              <span className="sd-term__cmd">{cmd}</span>
              {rest && <span className="sd-term__kw">{' '}{rest}</span>}
            </div>
          );
        })}
        {cursor && <span className="sd-term__cursor" />}
      </div>
    </div>
  );
}

/* ================================================================
   Reveal hook — scoped to component root
   ================================================================ */
function useReveal(rootRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll<Element>('.fade');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef]);
}

/* ================================================================
   Content data
   ================================================================ */
const STEPS = [
  { ic: 'fa-arrows-rotate',        t: 'Konto verbinden',   p: 'Direkte Anbindung an die Google Ads API. Performance-Daten werden nur auf deine Aktion hin abgerufen — du bestimmst den Datenstand.' },
  { ic: 'fa-wand-magic-sparkles',  t: 'KI schlägt vor',    p: 'Ein wöchentlicher Optimierungslauf erzeugt ein Change Set: Keywords, Anzeigentexte, Assets und Performance-Max-Gruppen — jeweils mit Begründung.' },
  { ic: 'fa-code-compare',         t: 'Prüfen & editieren', p: 'Auf der Werkbank siehst du jede Änderung im Vorher/Nachher-Diff. Annehmen, ablehnen oder direkt anpassen — Item für Item.' },
  { ic: 'fa-cloud-arrow-up',       t: 'Freigeben & Push',  p: 'Nur freigegebene Items gehen an Google Ads. Lückenloser Verlauf, jederzeit per Gegen-Änderung revidierbar.' },
] as const;

const FEATS = [
  { ic: 'fa-wand-magic-sparkles',  t: 'Vorschläge mit Begründung',   p: 'Jedes Item erklärt, warum es vorgeschlagen wird — gestützt auf Suchanfragenbericht, Konto-Benchmark und erwartete Wirkung.' },
  { ic: 'fa-code-compare',         t: 'Vorher/Nachher-Diff',         p: 'Keyword-Listen, RSA-Headlines und Beschreibungen werden Token-genau gegenübergestellt. Keine Blackbox.' },
  { ic: 'fa-id-card',              t: 'Kundenprofil als Wissensbasis',p: 'Ton, Dos und No-Gos je Konto halten jede Formulierung on-brand — z. B. „keine Rabatt-Sprache".' },
  { ic: 'fa-clipboard-list',       t: 'Terminprotokoll-Konformität', p: 'Entscheidungen aus Meetings werden dokumentiert. Die KI prüft Vorschläge gegen das, was vereinbart wurde.' },
  { ic: 'fa-table-cells-large',    t: 'Multi-Konto-Konsole',         p: 'Alle Mandanten in einer Übersicht — offene Vorschläge, letzter Datenstand und Ausgaben auf einen Blick.' },
  { ic: 'fa-clock-rotate-left',    t: 'Lückenloser Verlauf',         p: 'Wer hat was wann freigegeben oder revidiert? Jede Aktion ist nachvollziehbar und rückholbar.' },
] as const;

const TERM_LINES: TermLine[] = [
  { type: 'cmd', text: 'ads-optimizer run --account alpengluehen' },
  { type: 'out', text: '✓ Change Set KW 24 erzeugt · 7 Items' },
  { type: 'out', text: '  keywords · rsa · assets · pmax' },
  { type: 'cmd', text: 'review --diff' },
  { type: 'out', text: '→ 6 angenommen · 1 wartet auf Material' },
  { type: 'cmd', text: 'push --only=accepted' },
  { type: 'out', text: '✓ 6 von 6 an Google Ads übertragen' },
  { type: 'out', text: '  Verlauf aktualisiert · revidierbar' },
];

/* ================================================================
   Section components
   ================================================================ */
function Nav({ onEnterApp }: { onEnterApp?: () => void }) {
  return (
    <nav className="nav">
      <div className="wrap nav-in">
        <a className="brand" href="#top">
          <img src={logoSrc} alt="" />
          <div>
            <div className="bn">Ads Optimizer</div>
            <div className="bs">Berater-Konsole</div>
          </div>
        </a>
        <div className="nav-links">
          <a href="#ablauf">Ablauf</a>
          <a href="#funktionen">Funktionen</a>
          <a href="#kontrolle">Kontrolle</a>
          <a href="#konten">Konten</a>
        </div>
        <div className="nav-right">
          <span className="beta-pill"><span className="bdot" />Closed&nbsp;Beta</span>
          {onEnterApp && (
            <Btn variant="ghost" size="sm" onClick={() => onEnterApp()}>
              Anmelden
            </Btn>
          )}
          <Btn variant="primary" size="sm" href="#warteliste" icon={<i className="fa-solid fa-paper-plane" />}>
            Auf die Warteliste
          </Btn>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header className="hero" id="top">
      <div className="wrap hero-in">
        <div className="eyebrow">// Google Ads · KI-Optimierung</div>
        <h1 className="hero-h">
          Die KI schlägt vor.<br />
          <span className="grad">Du gibst frei.</span>
        </h1>
        <p className="hero-sub">
          Ads Optimizer erzeugt wöchentlich konkrete Optimierungs&shy;vorschläge für deine
          Google-Ads-Konten — jeder mit Begründung und Vorher/Nachher-Diff. Du prüfst,
          passt an und gibst frei. Erst dann geht etwas live.
        </p>
        <div className="hero-cta">
          <Btn variant="gradient" size="lg" href="#warteliste" icon={<i className="fa-solid fa-paper-plane" />}>
            Auf die Warteliste
          </Btn>
          <Btn variant="outline" size="lg" href="#ablauf" icon={<i className="fa-solid fa-arrow-down" />}>
            So funktioniert&apos;s
          </Btn>
        </div>
        <div className="hero-note">
          <i className="fa-solid fa-lock" />
          Closed Beta · Zugang nach Verfügbarkeit · keine Kreditkarte
        </div>
        <div className="scope-tags">
          <Tag icon={<i className="fa-solid fa-key" />}>Keywords</Tag>
          <Tag icon={<i className="fa-solid fa-align-left" />}>RSA-Texte</Tag>
          <Tag icon={<i className="fa-solid fa-image" />}>Bild-Assets</Tag>
          <Tag icon={<i className="fa-solid fa-film" />}>Video</Tag>
          <Tag icon={<i className="fa-solid fa-layer-group" />}>Performance Max</Tag>
        </div>
      </div>

      <div className="wrap showcase fade">
        <div className="browser">
          <div className="browser-bar">
            <span className="d d1" /><span className="d d2" /><span className="d d3" />
            <span className="browser-url">
              <i className="fa-solid fa-lock" />
              app.schababerle.digital/ads-optimizer
            </span>
          </div>
          <img src={werkbankSrc} alt="Ads Optimizer — Werkbank mit Optimierungs-Vorschlägen" />
        </div>
      </div>
    </header>
  );
}

function Ablauf() {
  return (
    <section className="sec" id="ablauf">
      <div className="wrap">
        <div className="sec-head center fade">
          <div className="sec-eyebrow">Der Ablauf</div>
          <h2 className="sec-h">Vier Schritte. Du bleibst am Steuer.</h2>
          <p className="sec-p">Von der Datenanbindung bis zum Push — ein klarer, prüfbarer Weg ohne Autopilot.</p>
        </div>
        <div className="steps fade">
          {STEPS.map((s, i) => (
            <div className="step" key={i}>
              <div className="step-n">0{i + 1}<span className="ln" /></div>
              <div className="step-ic"><i className={`fa-solid ${s.ic}`} /></div>
              <h3>{s.t}</h3>
              <p>{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Funktionen() {
  return (
    <section
      className="sec"
      id="funktionen"
      style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)' }}
    >
      <div className="wrap">
        <div className="sec-head fade">
          <div className="sec-eyebrow">Funktionen</div>
          <h2 className="sec-h">Optimierung, die du nachvollziehen kannst.</h2>
          <p className="sec-p">Substanz statt Automatik-Versprechen: jede Empfehlung ist begründet, sichtbar und an deine Markenregeln gebunden.</p>
        </div>
        <div className="features fade">
          {FEATS.map((f, i) => (
            <div className="feat" key={i}>
              <div className="feat-ic"><i className={`fa-solid ${f.ic}`} /></div>
              <h3>{f.t}</h3>
              <p>{f.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Kontrolle() {
  return (
    <section className="sec band theme-dark" id="kontrolle">
      <div className="wrap band-grid">
        <div className="fade">
          <div className="sec-eyebrow">Human-in-the-loop</div>
          <h2 className="sec-h">Nichts geht live ohne deine Freigabe.</h2>
          <p className="sec-p">Die KI arbeitet vor — entscheiden tust du. Volle Kontrolle über jeden Datenabruf, jedes Item und jeden Push.</p>
          <ul className="ctrl-list">
            <li>
              <span className="ci"><i className="fa-solid fa-hand" /></span>
              <div>
                <b>Manuelle Datenaktualisierung</b>
                <span>Performance-Daten werden nie im Hintergrund gezogen — der Datenstand ist immer der, den du angestoßen hast.</span>
              </div>
            </li>
            <li>
              <span className="ci"><i className="fa-solid fa-list-check" /></span>
              <div>
                <b>Item-genaue Freigabe</b>
                <span>Annehmen, ablehnen, abwählen oder editieren — pro Vorschlag. Massen-Aktionen sind optional, nie Pflicht.</span>
              </div>
            </li>
            <li>
              <span className="ci"><i className="fa-solid fa-rotate-left" /></span>
              <div>
                <b>Revidieren per Gegen-Änderung</b>
                <span>Eine gepushte Änderung lässt sich jederzeit sauber zurücknehmen — als nachvollziehbare Gegen-Änderung.</span>
              </div>
            </li>
            <li>
              <span className="ci"><i className="fa-solid fa-user-shield" /></span>
              <div>
                <b>Rollen &amp; Rechte</b>
                <span>Owner, Freigeber und Betrachter pro Konto. Wer freigeben darf, ist klar geregelt.</span>
              </div>
            </li>
          </ul>
        </div>
        <div className="fade">
          <Term
            title="ads-optimizer — push --account alpengluehen"
            lines={TERM_LINES}
          />
        </div>
      </div>
    </section>
  );
}

function Konten() {
  return (
    <section className="sec" id="konten">
      <div className="wrap split">
        <div className="fade">
          <div className="sec-eyebrow">Berater-Konsole</div>
          <h2 className="sec-h">Alle Mandanten an einem Ort.</h2>
          <p className="sec-p">
            Eine Übersicht für jedes betreute Konto — mit offenen Vorschlägen, letztem Datenstand
            und Ausgaben. Konto wechseln, prüfen, freigeben.
          </p>
          <ul className="split-list">
            <li><i className="fa-solid fa-check" />Offene Vorschläge je Konto auf einen Blick</li>
            <li><i className="fa-solid fa-check" />Pro Konto eigene Aktualisierungs-Intervalle</li>
            <li><i className="fa-solid fa-check" />Eigenes Kundenprofil &amp; eigene Markenregeln</li>
            <li><i className="fa-solid fa-check" />Schneller Kontowechsel ohne Kontextverlust</li>
          </ul>
        </div>
        <div className="split-visual fade">
          <div className="browser">
            <div className="browser-bar">
              <span className="d d1" /><span className="d d2" /><span className="d d3" />
              <span className="browser-url">
                <i className="fa-solid fa-lock" />
                app.schababerle.digital/konten
              </span>
            </div>
            <img src={dashboardSrc} alt="Ads Optimizer — Konten-Übersicht" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Warteliste() {
  const [email, setEmail] = useState('');
  const [accounts, setAccounts] = useState('');
  const [sent, setSent] = useState(false);
  const [pos, setPos] = useState(0);

  const submit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!email.trim() || !/.+@.+\..+/.test(email)) return;
    setPos(Math.floor(Math.random() * 40) + 47);
    setSent(true);
    try {
      const raw = localStorage.getItem('ado_waitlist') ?? '[]';
      const list = JSON.parse(raw) as Array<{ email: string; accounts: string; at: number }>;
      list.push({ email, accounts, at: Date.now() });
      localStorage.setItem('ado_waitlist', JSON.stringify(list));
    } catch (_) { /* ignore storage errors */ }
  };

  return (
    <section className="sec waitlist" id="warteliste">
      <div className="wrap">
        <div className="wl-card fade">
          <div className="wl-grid">
            <div>
              <span className="beta-pill" style={{ marginBottom: 18 }}>
                <span className="bdot" />Closed Beta · Plätze begrenzt
              </span>
              <h2 className="sec-h" style={{ marginTop: 16 }}>Sichere dir einen Beta-Platz.</h2>
              <p className="sec-p">
                Ads Optimizer ist aktuell in einer geschlossenen Beta. Trag dich auf die
                Warteliste ein — du bekommst Zugang, sobald ein Platz frei wird, und vorab
                eine kostenlose Erstanalyse deiner Konten.
              </p>
            </div>

            <div>
              {!sent ? (
                <form className="wl-form" onSubmit={submit}>
                  <Field
                    label="E-Mail"
                    type="email"
                    required
                    placeholder="name@firma.de"
                    icon={<i className="fa-solid fa-envelope" />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Field
                    label="Google-Ads-Konten (optional)"
                    placeholder="z. B. 2 Mandanten · E-Commerce"
                    icon={<i className="fa-solid fa-table-cells-large" />}
                    value={accounts}
                    onChange={(e) => setAccounts(e.target.value)}
                  />
                  <Btn variant="gradient" size="lg" type="submit" block icon={<i className="fa-solid fa-paper-plane" />}>
                    Platz auf der Warteliste sichern
                  </Btn>
                  <div className="wl-fine">$ Kein Spam. Wir melden uns nur, wenn ein Beta-Zugang für dich bereitsteht.</div>
                </form>
              ) : (
                <div className="wl-success is-visible">
                  <div className="ok-ic"><i className="fa-solid fa-check" /></div>
                  <h4>Du stehst auf der Warteliste.</h4>
                  <p>
                    Wir haben <b style={{ color: 'var(--text-body)' }}>{email}</b> notiert und melden uns,
                    sobald dein Zugang bereit ist.
                  </p>
                  <div className="wl-position"># Position {pos} · voraussichtlicher Zugang: nächste Beta-Welle</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Foot() {
  return (
    <footer className="foot">
      <div className="wrap">
        <div className="foot-in">
          <a className="brand" href="#top">
            <img src={logoSrc} alt="" />
            <div>
              <div className="bn">Ads Optimizer</div>
              <div className="bs">von schababerle.digital</div>
            </div>
          </a>
          <div className="foot-links">
            <a href="#ablauf">Ablauf</a>
            <a href="#funktionen">Funktionen</a>
            <a href="#kontrolle">Kontrolle</a>
            <a href="#warteliste">Warteliste</a>
            <a href="mailto:patrick@schababerle.digital">Kontakt</a>
          </div>
        </div>
        <div className="foot-meta">
          <span>$ schababerle.digital · Patrick Schababerle · Software Engineer · Nordschwarzwald</span>
          <span>© 2026 · Closed Beta</span>
        </div>
      </div>
    </footer>
  );
}

/* ================================================================
   Root export
   ================================================================ */
export default function Landing({ onEnterApp }: { onEnterApp?: () => void }) {
  const rootRef = useRef<HTMLDivElement>(null);
  useReveal(rootRef);

  return (
    <div className="ado-lp theme-light" ref={rootRef}>
      <Nav onEnterApp={onEnterApp} />
      <Hero />
      <Ablauf />
      <Funktionen />
      <Kontrolle />
      <Konten />
      <Warteliste />
      <Foot />
    </div>
  );
}
